import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  useAuth,
  useInvitationPreview,
  useMyUnfinishedInvitations,
} from '@/hooks';
import { useUserStore } from '@/stores/userStore';
import {
  clearInvitationToken,
  rememberInvitationToken,
  wasInvitationHeroSeen,
} from '@/lib/invitationContext';
import { resolveOnboarding } from '@/lib/onboardingFlow';
import type { InviteeOnboardingStep } from '@/services/invitation.service';
import type { CaregiverRole } from '@/types';
import {
  clearPostAuthRedirect,
  peekPostAuthRedirect,
} from '@/lib/postAuthRedirect';
import { InviteeLinkState } from './onboarding/InviteeLinkState';
import { InviteeOnboardingFlow } from './onboarding/InviteeOnboardingFlow';
import {
  INVITEE_COPY,
  INVITEE_FALLBACK_ADMIN_NAME,
} from './onboarding/inviteeOnboardingCopy';
import { OnboardingEntry } from './onboarding/OnboardingEntry';
import { useHouseholdlessAccount } from '@/hooks/useHouseholdlessAccount';
import { StepWelcome } from './onboarding/StepWelcome';

function inviterFirstName(displayName: string | null): string {
  return displayName?.trim().split(/\s+/)[0] || INVITEE_FALLBACK_ADMIN_NAME;
}

export function Onboarding() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  // Pinned once per mount. resolveOnboarding() used to default `now` to a
  // fresh `new Date()` on every render, so an invitee sitting on the carousel
  // was re-evaluated against the clock continuously — see isExpired() in
  // lib/onboardingFlow.ts. Expiry is decided when the flow is entered, not
  // continuously while the invitee is in it.
  const [resolvedAt] = useState(() => new Date());
  // ✕ on the Hero alerts hides the alert and nothing else — AC8's
  // "dismiss-only, no routing beyond the Hero".
  const [arrivalAlertDismissed, setArrivalAlertDismissed] = useState(false);
  const [expiredAlertDismissed, setExpiredAlertDismissed] = useState(false);
  const { inviteToken = null } = useParams<{ inviteToken: string }>();
  const { preview, loading, error, refresh } =
    useInvitationPreview(inviteToken);
  const [heroAcknowledgedToken, setHeroAcknowledgedToken] = useState<
    string | null
  >(() =>
    inviteToken && wasInvitationHeroSeen(inviteToken) ? inviteToken : null,
  );
  const onboardingCompletedAt = useUserStore(
    (state) => state.onboardingCompletedAt,
  );
  const householdless = useHouseholdlessAccount(
    !authLoading && !!user && onboardingCompletedAt !== null,
  );
  // Latched for this mount: once the fork is shown to a household-less
  // account, creating a household from it flips the hook to 'has' — which
  // must not bounce the parent to /today mid-onboarding. The flow ends with
  // its own hard navigation to /today.
  const [householdlessFork, setHouseholdlessFork] = useState(false);
  useEffect(() => {
    if (householdless === 'none') setHouseholdlessFork(true);
  }, [householdless]);
  const showHouseholdlessFork = householdlessFork || householdless === 'none';
  // Only for someone who has not finished onboarding — see the block near the
  // admin fall-through for why that audience is the right one. Membership is
  // deliberately NOT part of this test: accepting an invitation seats the
  // member several steps before their onboarding ends, so a mid-flow invitee
  // holds one, and excluding them is what left them with nowhere to go. Hooks
  // run unconditionally, so this sits with the others rather than in a branch.
  const { invitations: pendingInvitations, loading: pendingLoading } =
    useMyUnfinishedInvitations(
      !inviteToken && !!user && onboardingCompletedAt === null,
    );

  useEffect(() => {
    if (inviteToken) rememberInvitationToken(inviteToken);
  }, [inviteToken]);

  useEffect(() => {
    if (preview?.state === 'completed') {
      clearInvitationToken();
      navigate('/today', { replace: true });
    }
  }, [navigate, preview?.state]);

  const alreadyResolved =
    !inviteToken && !!user && onboardingCompletedAt !== null;
  useEffect(() => {
    if (alreadyResolved) clearPostAuthRedirect();
  }, [alreadyResolved]);

  if (inviteToken) {
    if (loading || authLoading) {
      return <InviteeLinkState state="loading" />;
    }
    if (error) {
      return (
        <InviteeLinkState
          state="error"
          onRetry={() => void refresh()}
        />
      );
    }
    if (!preview || preview.state === 'unavailable') {
      return (
        <StepWelcome
          alert={
            expiredAlertDismissed
              ? undefined
              : {
                  tone: 'error',
                  title: INVITEE_COPY.hero.unavailableTitle,
                  body: INVITEE_COPY.hero.unavailableBody,
                  onDismiss: () => setExpiredAlertDismissed(true),
                }
          }
          onComplete={() => {}}
        />
      );
    }
    if (preview.state === 'expired') {
      // AC8: the Hero shows the expired alert, dismiss-only, "no routing
      // beyond the Hero". Both Figma frames keep the full Hero (headline,
      // Continue with Google, waitlist, terms) and only swap the alert tone,
      // so this must NOT be a full-screen takeover and ✕ must not navigate.
      return (
        <StepWelcome
          alert={
            expiredAlertDismissed
              ? undefined
              : {
                  tone: 'error',
                  title: INVITEE_COPY.hero.expiredTitle,
                  body: INVITEE_COPY.hero.expiredBody(preview.expiry_label),
                  onDismiss: () => setExpiredAlertDismissed(true),
                }
          }
          onComplete={() => {}}
        />
      );
    }
    if (preview.state === 'completed') {
      return <InviteeLinkState state="loading" />;
    }

    const { invitation } = preview;
    const heroAcknowledged =
      heroAcknowledgedToken === inviteToken ||
      wasInvitationHeroSeen(inviteToken);
    const accountMismatch =
      user?.email !== undefined &&
      user.email.toLowerCase() !== invitation.invitee_email.toLowerCase();

    if (!heroAcknowledged || !user || accountMismatch) {
      const householdName = invitation.household_name?.trim() || 'a household';
      const inviter = inviterFirstName(
        invitation.inviter?.display_name ?? null,
      );
      return (
        <StepWelcome
          invitation={{
            householdName,
            inviterFirstName: inviter,
            inviteeEmail: invitation.invitee_email,
          }}
          alert={
            arrivalAlertDismissed
              ? undefined
              : {
                  tone: 'invited',
                  // tracker 1.9 / 1.91, verbatim.
                  title: INVITEE_COPY.hero.arrivalTitle(householdName),
                  body: INVITEE_COPY.hero.arrivalBody(
                    inviter,
                    invitation.invitee_email,
                  ),
                  onDismiss: () => setArrivalAlertDismissed(true),
                }
          }
          onComplete={() => setHeroAcknowledgedToken(inviteToken)}
        />
      );
    }

    const resolution = resolveOnboarding({
      invitation: {
        id: invitation.id,
        role: invitation.role,
        status: invitation.status,
        expiresAt: invitation.expires_at,
        onboardingCompletedAt: null,
      },
      adminOnboardingCompletedAt: onboardingCompletedAt,
      now: resolvedAt,
    });

    if (resolution.destination === 'invite-deferred') {
      return (
        <InviteeLinkState
          state="deferred"
          onDismiss={() => {
            clearInvitationToken();
            // Sign out rather than navigating to /today. A co-admin invitee
            // has an account but NO membership (only "Accept invite" creates
            // one), so /today would bounce them into ADMIN onboarding and
            // they would create their own household instead of joining the
            // inviter's.
            void signOut().finally(() => navigate('/', { replace: true }));
          }}
        />
      );
    }
    if (resolution.destination === 'invite-expired') {
      return (
        <StepWelcome
          alert={
            expiredAlertDismissed
              ? undefined
              : {
                  tone: 'error',
                  title: INVITEE_COPY.hero.expiredTitle,
                  body: INVITEE_COPY.hero.expiredBody(invitation.expiry_label),
                  onDismiss: () => setExpiredAlertDismissed(true),
                }
          }
          onComplete={() => {}}
        />
      );
    }
    if (resolution.destination !== 'invitee') {
      return (
        <Navigate
          to="/today"
          replace
        />
      );
    }

    return (
      <InviteeOnboardingFlow
        user={user}
        invitation={invitation}
        flow={resolution.flow}
        initialState={preview.state}
      />
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <StepWelcome onComplete={() => {}} />;
  }

  // This must stay the EXACT complement of ProtectedRoute's onboarding gate.
  // It used to also send any seated non-PRIMARY member to /today, but
  // ProtectedRoute admits nobody whose users.onboarding_completed_at is null —
  // so an invited Editor bounced back here, and back to /today, forever. A
  // redirect loop never paints: that pair was the invitee's blank screen. Two
  // guards deciding "is this user onboarded?" by different rules is the bug;
  // one rule, used by both, is the fix. A seated member who has finished is
  // covered by the flag itself, which the invitee flow now stamps.
  //
  // One exception, decided by the same hook ProtectedRoute uses: an onboarded
  // account that belongs to NO household (left its only family, was removed,
  // or the household was deleted) would land in an empty app. It falls
  // through to the create-or-join fork below instead.
  if (
    onboardingCompletedAt !== null &&
    !showHouseholdlessFork &&
    householdless === 'checking'
  ) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }
  if (onboardingCompletedAt !== null && !showHouseholdlessFork) {
    return (
      <Navigate
        to={peekPostAuthRedirect() ?? '/today'}
        replace
      />
    );
  }
  if (onboardingCompletedAt !== null) {
    // Household-less: straight to the fork. The pending-invitation branch
    // below is for accounts that never finished onboarding; this one already
    // did, so it starts a family or waits to be invited from the fork.
    return <OnboardingEntry user={user} />;
  }

  // ── Invite arrived without its token ──────────────────────────────────────
  //
  // Everything above this point requires the token to be in the URL. Without
  // it an invited Editor/Viewer was indistinguishable from a brand-new user:
  // they got the full admin onboarding — founder letter and all — and went on
  // to create THEIR OWN household while the inviter's invitation stayed
  // PENDING. Reachable by signing in from the landing page instead of the
  // emailed link, or on a second device, or in another browser.
  //
  // Reaching here means the user has NOT completed onboarding. That is the
  // right audience: a parent owns exactly one household but may belong to
  // others as Editor or Viewer, so an established user with a pending
  // invitation elsewhere is a normal state — they accept it from the in-app
  // notification, not by being pulled back through onboarding.
  //
  // They may already hold a membership. Accepting seats the member at the
  // START of the carousel, so anyone who closed the tab mid-flow arrives here
  // seated, with an ACCEPTED invitation, and resumes at their saved step.
  if (pendingLoading) {
    return <InviteeLinkState state="loading" />;
  }
  const pending =
    pendingInvitations.length === 1 ? pendingInvitations[0] : undefined;
  if (pending?.household_id) {
    const householdId = pending.household_id;
    const resolution = resolveOnboarding({
      invitation: {
        id: pending.id,
        role: pending.role as Exclude<CaregiverRole, 'PRIMARY'>,
        status: pending.status,
        expiresAt: pending.expires_at,
        onboardingCompletedAt: null,
      },
      adminOnboardingCompletedAt: onboardingCompletedAt,
      now: resolvedAt,
    });
    // Only the invitee destination is honoured here. An expired or deferred
    // invitation falls through to admin onboarding: the token path owns those
    // screens, and stranding someone who cannot proceed would be worse than
    // letting them create the household they came for.
    if (resolution.destination === 'invitee') {
      return (
        <InviteeOnboardingFlow
          user={user}
          invitation={{
            id: pending.id,
            invitee_email: pending.invitee_email,
            role: pending.role as 'CO_ADMIN' | 'EDITOR' | 'VIEWER',
            // Carried through, not hardcoded. An ACCEPTED row means the
            // invitee already has their seat and is part-way through the
            // carousel; re-showing the Accept screen would ask them to accept
            // an invitation they already consumed.
            status: pending.status === 'ACCEPTED' ? 'ACCEPTED' : 'PENDING',
            expires_at: pending.expires_at,
            onboarding_step:
              pending.onboarding_step as InviteeOnboardingStep | null,
            household_id: householdId,
            household_name: pending.household_name,
            // Not joined by the invitee-scoped query — that join needs the
            // service role, which only invitations-preview has. The Hero falls
            // back to INVITEE_FALLBACK_ADMIN_NAME, so the inviter reads
            // generically. A copy degradation, not a broken flow.
            inviter: null,
          }}
          flow={resolution.flow}
          initialState={pending.status === 'ACCEPTED' ? 'accepted' : 'pending'}
        />
      );
    }
  }
  // More than one pending invitation needs a screen that does not exist yet —
  // choosing for the user could drop them into the wrong family's schedule,
  // which is hard to undo. Falls through to admin onboarding, unchanged, until
  // YAR-336 is designed.

  return <OnboardingEntry user={user} />;
}
